import '@remento-analytics/host/@types';

import { ABTestRules, ABTestService } from '@remento/ab-testing/ab-test.types';
import { getABTestAnalyticsProperties } from '@remento/ab-testing/ab-test.utils';
import { registerActivityHandler } from '@remento/web-analytics-router/sources/activity/activity-handler';
import { registerActivityPlugin } from '@remento/web-analytics-router/sources/activity/activity-plugin';
import { registerClickHandler } from '@remento/web-analytics-router/sources/click/click-handler';
import { registerClickObserver } from '@remento/web-analytics-router/sources/click/click-observer';
import { registerEmailFormHandler } from '@remento/web-analytics-router/sources/form/form-handler';
import { registerActivityTransform } from '@remento/web-analytics-router/transforms/activity';
import { registerCookiesTransform } from '@remento/web-analytics-router/transforms/cookies';
import { registerPageTransform } from '@remento/web-analytics-router/transforms/page';
import { waitForWisepopsLoaded } from '@remento/web-analytics-router-wisepops/init';
import { registerWisepopsEmailFormsObserver } from '@remento/web-analytics-router-wisepops/sources/form-observer';
import { waitForDocumentLoaded } from '@remento/web-utils/dom/document';
import { initializeHost } from '@remento-analytics/host/init';

import { registerWebflowEmailFormsObserver } from './analytics-router/form-observer.js';

const AB_EXPERIMENTS_RULES = {
  ['try-recording']: {
    defaultVariant: 'control' as const,
    variants: {
      a: [{ weight: 0.0 }],
    },
  },
} as const satisfies ABTestRules;


function setupTryRecordingExperiment(experiment: 'control' | 'a') {
  switch (experiment) {
    case 'a':
      document.querySelectorAll('a[data-analytics-event-prop-cta="try-recording"]').forEach((el) => {
        if (el.tagName.toLowerCase() !== 'a') {
          return;
        }
        const anchor = el as HTMLAnchorElement;
        anchor.href = 'https://web.remento.co/recording/try';
      });
      break;
    case 'control':
      break;
  }
}

async function setupExperiments(userID: string, ab: ABTestService<typeof AB_EXPERIMENTS_RULES>) {
  const tryRecordingVariant = ab.getUserVariant(userID, 'try-recording', navigator.userAgent);
  setupTryRecordingExperiment(tryRecordingVariant);
}

async function initialize() {
  const source = process.env.RO_ANALYTICS_SOURCE!;
  await initializeHost(AB_EXPERIMENTS_RULES, async (router, analytics, ab, debug) => {
    const userID = analytics.getUser().id;

    registerActivityPlugin(router, source, 20 * 1000, 10 * 1000);

    const abProperties = getABTestAnalyticsProperties(userID, ab);
    router.registerTransform('analytics-transform', (event, payload) => ({
      ...payload,
      properties: { ...payload.properties, ...abProperties },
    }));
    registerCookiesTransform(router);
    registerPageTransform(router, source, window.ANALYTICS_PAGE, window.ANALYTICS_PROPERTIES);
    registerActivityTransform(router);

    registerClickHandler(router, analytics);
    registerEmailFormHandler(router, analytics);
    registerActivityHandler(router, analytics);

    const docLoadedPromise = waitForDocumentLoaded().then(() => {
      registerClickObserver(router, debug);
      registerWebflowEmailFormsObserver(router, debug);
    });
    const wisepopsLoadedPromise = waitForWisepopsLoaded().then(() => {
      registerWisepopsEmailFormsObserver(router, debug);
    });
    debug?.registerInitDependency(docLoadedPromise);
    debug?.registerInitDependency(wisepopsLoadedPromise);

    await setupExperiments(userID, ab);
  });
}

(() => {
  Promise.resolve()
    .then(() => initialize())
    .catch((err) => console.error('INIT_ERROR', err));
})();
